import { Space, TimePicker, Typography } from 'antd'
import { useWatch } from 'antd/lib/form/Form'
import { useCIMSNotificationsPanelContext } from 'components/CIMSNotifications/components/CIMSNotificationsPanelContext'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { useEffect, useState } from 'react'
import { DailyItem, FrequencyDefault, FrequencyItem } from '@cims-online/shared'
import CIMSFormItem from 'components/CIMSFormItem'
import { HelpMessage } from 'utils/constants'
import CIMSSelect from 'components/CIMSSelect'

const FrequencyFormItems: React.VFC = () => {
  const { form } = useCIMSNotificationsPanelContext()
  const dailyOptions = useOptions({
    items: DailyItem.values,
    value: 'value',
    label: 'label',
    sort: false,
  })
  const frequencyOptions = useOptions({
    items: FrequencyItem.values,
    value: 'type',
    label: 'label',
    sort: false,
  })
  const defaultDay = DailyItem.findOrFail(
    ({ value }) => value === FrequencyDefault.DAY
  ).label

  const [timeFormat, setTimeFormat] = useState<'h' | 'mm' | 'h:mm a'>('h:mm a')
  const [timeDefaultMessage, setTimeDefaultMessage] = useState<string>('')
  const [minOrHourly, setMinOrHourly] = useState<boolean>(false)
  const [units, setUnits] = useState<'minute(s)' | 'hour(s)' | ''>('')
  const [timePlaceholder, setTimePlaceholder] = useState<
    'Select minutes' | 'Select hours' | 'Select time'
  >('Select time')
  const frequencyWatch = useWatch<FrequencyItem['type']>('frequencyType', form)

  useEffect(() => {
    if (frequencyWatch === FrequencyItem.MinuteFrequencyItem.type) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
      setMinOrHourly(true)
      setUnits('minute(s)')
      setTimePlaceholder('Select minutes')
      setTimeFormat('mm')
      setTimeDefaultMessage(`${FrequencyDefault.MINUTES} minutes`)
    } else if (frequencyWatch === FrequencyItem.HourlyFrequencyItem.type) {
      setMinOrHourly(true)
      setUnits('hour(s)')
      setTimePlaceholder('Select hours')
      setTimeFormat('h')
      setTimeDefaultMessage(
        `${FrequencyDefault.HOURS} hour${FrequencyDefault.HOURS > 1 ? 's' : ''}`
      )
    } else {
      setMinOrHourly(false)
      setUnits('')
      setTimePlaceholder('Select time')
      setTimeFormat('h:mm a')
      setTimeDefaultMessage(FrequencyDefault.TIME)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequencyWatch])

  return (
    <Space size={'middle'}>
      <CIMSFormItem
        name="frequencyType"
        label="Frequency"
        help={HelpMessage.IdentifiedFieldIssues.Notifications.Frequency(
          frequencyWatch
        )}
      >
        <CIMSSelect
          simple
          style={{ width: '100px' }}
          options={frequencyOptions}
          onChange={() => {
            form.setFieldsValue({
              day: undefined,
              time: undefined,
            })
          }}
        />
      </CIMSFormItem>
      {frequencyWatch === FrequencyItem.WeeklyFrequencyItem.type && (
        <CIMSFormItem
          name="day"
          label="On"
          help={HelpMessage.IdentifiedFieldIssues.Notifications.Day}
          defaultMessage={defaultDay}
        >
          <CIMSSelect
            simple
            options={dailyOptions}
            placeholder="Select day"
            style={{ width: '135px' }}
          />
        </CIMSFormItem>
      )}
      {frequencyWatch && (
        <Space size={5}>
          <CIMSFormItem
            name="time"
            defaultMessage={timeDefaultMessage}
            label={minOrHourly ? 'Every' : 'At'}
            help={HelpMessage.IdentifiedFieldIssues.Notifications.Time(
              frequencyWatch
            )}
          >
            <TimePicker
              style={{ width: 155 }}
              format={timeFormat}
              minuteStep={15}
              showNow={false}
              hideDisabledOptions
              placeholder={timePlaceholder}
              disabledTime={() => ({
                disabledMinutes: () =>
                  frequencyWatch === FrequencyItem.MinuteFrequencyItem.type
                    ? [0]
                    : [],
              })}
              use12Hours={!minOrHourly}
            />
          </CIMSFormItem>
          <Typography style={{ marginBottom: 24 }}>{units}</Typography>
        </Space>
      )}
    </Space>
  )
}

export default FrequencyFormItems
